/**
 * The admin-awareness banner (board-approval design § Admin awareness) — present only for an admin
 * viewer whose shell carries a positive `data-board-pending` count (GameController's non-creating
 * `isAdmin` check; absent entirely for everyone else, including an unconfigured store). A plain
 * navigation to `/admin`, not a fetched overlay — the admin page is Andre's own dedicated surface,
 * not in-game chrome, so there's nothing to keep alive underneath it. The visual was calibrated
 * live with Andre at build (2026-07-19, per the design's shape-live marker): slate-glass panel
 * with an amber spine — "polite but firm", not the solid-amber shout of the first pass.
 * Split off game-start-ui.js (a size-budget split, same precedent as game-start-wordmark.js /
 * game-start-mode-label.js — one file per self-contained start-screen builder).
 */
import { t } from "../i18n.js";

/**
 * Builds the banner, or returns null when there's nothing pending — the caller skips appending it
 * entirely rather than mounting an empty/hidden element.
 */
export function buildBoardPendingBanner() {
  const pending = document.getElementById("skat-app")?.dataset.boardPending;
  const count = pending == null ? 0 : Number(pending);
  if (!(count > 0)) return null;
  const banner = document.createElement("a");
  banner.className = "game-start-pending-banner";
  banner.href = "/admin";
  banner.appendChild(buildPendingBannerIcon());
  const text = document.createElement("span");
  text.className = "game-start-pending-banner-text";
  text.textContent = t("gameStartBoardPendingBanner", count);
  banner.appendChild(text);
  return banner;
}

function buildPendingBannerIcon() {
  const wrap = document.createElement("span");
  wrap.className = "game-start-pending-banner-icon";
  wrap.setAttribute("aria-hidden", "true");
  wrap.appendChild(createBellIcon());
  return wrap;
}

/** A plain bell mark — single-caller inline SVG (CODING.md locality-over-shared-utilities; one
 *  banner is not a reason to add a family under icons/). Body + clapper only, stroke-based so it
 *  recolors with the banner's `color` via currentColor. */
function createBellIcon() {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  // Fixed 28px, unlike this screen's other icons — no elastic --gs-ux CSS override (the CSS
  // section is at its size budget; first-pass sizing per this module's header, Andre recalibrates
  // the whole banner live).
  svg.setAttribute("width", "28");
  svg.setAttribute("height", "28");
  svg.setAttribute("viewBox", "0 0 48 48");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "3");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  const body = document.createElementNS("http://www.w3.org/2000/svg", "path");
  body.setAttribute(
    "d",
    "M24 6 C17 6 13 11 13 19 L13 26 L8 33 L40 33 L35 26 L35 19 C35 11 31 6 24 6 Z",
  );
  svg.appendChild(body);
  const clapper = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "path",
  );
  clapper.setAttribute(
    "d",
    "M19 38 C19 41.5 21.5 44 24 44 C26.5 44 29 41.5 29 38",
  );
  svg.appendChild(clapper);
  return svg;
}
